import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SurveyFooter from './SurveyFooter';
import SurveyTabContainer from './SurveyTabContainer';
import { Flex, Box, Text, TabsLayout, TabPanel, useDisclosure } from '@televet/kibble-ui';
import { RouteBasePaths } from 'routes';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import { useSurveySettingsContext } from '../context/SurveySettingsContext';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { Role, SurveyType } from 'shared/types/graphql';
import { SurveySettingsActions } from '../context/SurveySettingsReducer';
import UnsavedFormChangesModal from 'pages/Forms/components/UnsavedFormChangesModal';
import { Prompt, useHistory, useLocation } from 'react-router-dom';
import { Location } from 'history';
import useClinicUser from 'shared/hooks/useClinicUser';
import { quickSightRelativePath } from 'pages/Reporting';
import { QUICKSIGHT_DASHBOARD_URL } from 'pages/Reporting/QuickSightReports/embedDashboard';

export enum DefaultNegativeStatusNames {
  NPS = 'Detractor [NPS]',
  CSAT = 'Dissatisfied [CSAT]',
}

export enum DefaultPositiveStatusNames {
  NPS = 'Promoter [NPS]',
  CSAT = 'Satisfied [CSAT]',
}

const SurveyContainer = (): JSX.Element => {
  const { appointmentTypeSettings, isLoading, changedSettings, dispatch } = useSurveySettingsContext();
  const { isFeatureEnabled } = useFeatureFlag();
  const { clinicUser } = useClinicUser();
  const isAdmin =
    useMemo(() => clinicUser?.vetInfo?.roles?.some((role) => role.role === Role.Admin), [clinicUser]) ||
    clinicUser?.isAdmin;

  const surveyType = useMemo(() => {
    return isFeatureEnabled(FeatureFlagName.NpsSurvey) ? SurveyType.Nps : SurveyType.Csat;
  }, [isFeatureEnabled]);

  useEffect(() => {
    dispatch({ type: SurveySettingsActions.UPDATE_SURVEY_TYPE, payload: surveyType });
  }, [dispatch, surveyType]);

  const {
    isOpen: isUnsavedChangesOpen,
    onOpen: onUnsavedChangesOpen,
    onClose: onUnsavedChangesClose,
  } = useDisclosure();
  const location = useLocation();
  const [lastLocation, setLastLocation] = useState<Location>(location);
  const history = useHistory();
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [shouldUnload, setShouldUnload] = useState(false);

  const hasUnsavedChanges = useMemo(
    () =>
      Object.values(changedSettings).some((v) => v === true) ||
      !!changedSettings.surveyAppointmentTypeSettingIds.length,
    [changedSettings],
  );

  const handleBlockedNavigation = (location: Location): boolean => {
    if (!confirmedNavigation && hasUnsavedChanges) {
      setLastLocation(location);
      setShouldUnload(true);
      onUnsavedChangesOpen();
      return false;
    }
    return true;
  };

  const onConfirm = (): void => {
    if (hasUnsavedChanges) onUnsavedChangesClose();
    setConfirmedNavigation(true);
    setShouldUnload(false);
  };

  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      history.push(lastLocation?.pathname);
      setConfirmedNavigation(false);
    }
  }, [confirmedNavigation, lastLocation, history, location]);

  useEffect(() => {
    const unload = (event: BeforeUnloadEvent): void => {
      if (hasUnsavedChanges && !shouldUnload) {
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
        event.returnValue = ''; // deprecated-ish
        event.preventDefault(); // not fully supported yet
      }
    };

    window.addEventListener('beforeunload', unload);

    return (): void => window.removeEventListener('beforeunload', unload);
  }, [shouldUnload, hasUnsavedChanges]);

  const handleDashboardClick = useCallback(() => {
    switch (surveyType) {
      case SurveyType.Nps:
        history.push(
          `${RouteBasePaths.Reporting}${quickSightRelativePath}/${QUICKSIGHT_DASHBOARD_URL.NPS_Post_Appointment_Survey}`,
        );
        break;
      case SurveyType.Csat:
        history.push(
          `${RouteBasePaths.Reporting}${quickSightRelativePath}/${QUICKSIGHT_DASHBOARD_URL.CSAT_Post_Appointment_Survey}`,
        );
        break;
    }
  }, [history, surveyType]);

  const tabPanels = [
    <TabPanel key={1} h="100%" p={0} flex="1 1 auto">
      <Flex w="100%" h="100%" direction="column">
        {!appointmentTypeSettings.length && !isLoading ? (
          <Box m={5} w="100%" h="100%">
            <Text>You must have appointment types defined before you can use the Survey feature.</Text>
          </Box>
        ) : (
          <SurveyTabContainer />
        )}
        <SurveyFooter unsavedChanges={hasUnsavedChanges} />
      </Flex>
    </TabPanel>,
    <TabPanel key={2}>
      <Flex justifyContent="center">
        <LoadingIndicator />
      </Flex>
    </TabPanel>,
  ];

  return (
    <Flex
      borderTopWidth="1px"
      borderTopColor="border.default"
      alignItems="stretch"
      justifyContent="space-between"
      direction="column"
      h="100%"
      overflow="hidden"
    >
      {!!isAdmin ? (
        <>
          <TabsLayout
            tabList={[{ label: 'Survey' }, { label: `${surveyType} Dashboard`, onClick: handleDashboardClick }]}
            tabPanels={tabPanels}
            tabPanelsStyle={{
              h: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              justifyContent: 'center',
              bg: 'background.default',
            }}
            m={5}
            mb={0}
            display="flex"
            flexDirection="column"
            flex="1 1 auto"
            justifyContent="flex-start"
            overflow="hidden"
            index={0}
          />
          <Prompt when={hasUnsavedChanges} message={handleBlockedNavigation} />
          <UnsavedFormChangesModal
            isOpen={isUnsavedChangesOpen}
            onClose={onUnsavedChangesClose}
            onConfirm={onConfirm}
          />
        </>
      ) : (
        <Box m={5} w="100%" h="100%">
          <Text>You must be an admin user to view this page.</Text>
        </Box>
      )}
    </Flex>
  );
};

export default SurveyContainer;
